import { useNavigate } from '@tanstack/react-router'
import { useToast } from '@workspace/ui/components/toast'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useBasePath } from '../../../../hooks/use-base-path'
import { trackEvent } from '../../../../lib/gtag'
import { copy } from '../../copy/investor-copy'
import {
  useEditFollow,
  useFollowStartupMutation,
  useReopenPass,
  useShareStartupMutation,
} from '../../hooks/use-investor-assistant'
import { PassWindow } from '../search/pass-window'
import { type ShareDraft, ShareWindow } from '../search/share-window'
import { AssessmentCard } from './assessment-card'
import { AssessmentWindow } from './assessment-window'
import { FollowWindow } from './follow-window'
import { FollowingForCard } from './following-for-card'
import { NoFollowCards } from './no-follow-cards'
import { NoteCard } from './note-card'
import { PassedCard } from './passed-card'
import { LatestUpdatesCard, SinceCard } from './since-card'
import { StopDialog } from './stop-dialog'
import type { CardActions, CardVariant, Relationship, RelationshipUpdate } from './types'

type Overlay = 'edit' | 'follow' | 'stop' | 'assessment' | 'pass' | null

/** What a follow's outcome says, as a toast. */
export const followToast = (
  outcome: { status: string; waitingReason: string | null },
  startup: string
): string => {
  if (outcome.status === 'WAITING') {
    return copy(
      outcome.waitingReason === 'CHECK' ? 'toast.follow.waitingCheck' : 'toast.follow.waitingPlace'
    )
  }
  return copy('toast.follow.active', { startup })
}

const allowanceLine = (allowance: Relationship['allowance']): string =>
  copy(allowance.plan === 'FREE' ? 'card.allowance.free' : 'card.allowance.paid', {
    used: allowance.used,
    limit: allowance.limit,
  })

/**
 * What the cards of a startup can do, and the windows they open (edit, follow, stop, the read).
 * The side panel and the startup page share it, so a card means the same thing in both.
 */
export function useRelationshipActions({
  startupId,
  data,
  openProfile,
  onBack,
  shareToken,
  openSource,
  openUpdate,
  startNote,
  startFollow,
  onPassChange,
  onFollowChange,
}: {
  startupId: string
  data: Relationship | undefined
  /** Opens the startup's profile; null when there is no link to open. */
  openProfile: (() => void) | null
  onBack: () => void
  /** The link the investor holds, when they have one: a follow is made from it, which works for a startup in stealth. */
  shareToken?: string
  /** Where a "View source" goes; without one it opens the profile. */
  openSource?: (update: RelationshipUpdate) => void
  /** Where "Open the update" goes: the update that met the condition, by its quote. */
  openUpdate?: (quote: string) => void
  /** Start with the note open for writing. */
  startNote?: boolean
  /** Open the follow window as soon as the startup loads (a pass window's "Follow instead"). */
  startFollow?: boolean
  /** Tells the search row that a pass was made or undone here. */
  onPassChange?: (startupId: string, passed: boolean) => void
  /** Tells the search row that a follow started or stopped here. */
  onFollowChange?: (startupId: string, following: boolean) => void
}): {
  actions: CardActions
  dialogs: ReactNode
  note: { editing: boolean; setEditing: (editing: boolean) => void }
} {
  const navigate = useNavigate()
  const basePath = useBasePath()
  const [overlay, setOverlay] = useState<Overlay>(null)
  const [suggested, setSuggested] = useState<string | null>(null)
  const [anchor, setAnchor] = useState<Element | null>(null)
  const [noteEditing, setNoteEditing] = useState(startNote ?? false)
  const [shareDraft, setShareDraft] = useState<ShareDraft | null>(null)
  const editFollow = useEditFollow()
  const followStartup = useFollowStartupMutation()
  const reopenPass = useReopenPass()
  const share = useShareStartupMutation()
  const toast = useToast()
  const followRequested = useRef(false)
  const close = () => setOverlay(null)

  const actions: CardActions = {
    onEditFollow: (from) => {
      setAnchor(from ?? null)
      setOverlay('edit')
    },
    onWriteNote: () => setNoteEditing(true),
    onCancelFollow: () => setOverlay('stop'),
    onOpenUpdate: () => {
      trackEvent('update_source_opened', { startup_id: startupId })
      const quote = data?.follow?.conditionMet?.quote
      if (openUpdate && quote) openUpdate(quote)
      else openProfile?.()
    },
    onAllUpdates: () => openProfile?.(),
    onSeePlans: () => {
      trackEvent('see_plans_clicked', { startup_id: startupId })
      navigate({ to: `${basePath}/wallet` })
    },
    onBackToFollowing: onBack,
    onUndoPass: () =>
      reopenPass.mutate(
        { path: { startupId } },
        {
          onSuccess: () => {
            onPassChange?.(startupId, false)
            toast(copy('toast.undoPass.done'))
          },
          onError: () => toast(copy('toast.undoPass.error')),
        }
      ),
    onPass: () => setOverlay('pass'),
    onShare: () =>
      share.mutate(
        { path: { startupId } },
        { onSuccess: (response) => setShareDraft(response.data) }
      ),
    onFollow: (suggestion, from) => {
      setSuggested(suggestion ?? null)
      setAnchor(from ?? null)
      setOverlay('follow')
    },
    onConnectAi: () => navigate({ to: `${basePath}/connect-ai` }),
    onOpenAssessment: () => setOverlay('assessment'),
    onViewSource: (update) => {
      trackEvent('update_source_opened', { startup_id: startupId })
      if (openSource) openSource(update)
      else openProfile?.()
    },
  }

  // The pass window's "Follow instead" lands here: the follow window opens once, when the startup has loaded.
  useEffect(() => {
    if (!startFollow || !data || followRequested.current) return
    followRequested.current = true
    if (data.state === 'NONE') actions.onFollow()
  }, [startFollow, data, actions])

  const dialogs = data && (
    <>
      <PassWindow
        startup={overlay === 'pass' ? { id: startupId, name: data.startup.name } : null}
        onClose={close}
        onPassed={(passedId) => onPassChange?.(passedId, true)}
        onFollowInstead={() => {
          close()
          actions.onFollow()
        }}
      />
      <ShareWindow draft={shareDraft} onClose={() => setShareDraft(null)} />
      {overlay === 'edit' && data.follow && (
        <FollowWindow
          open
          mode="edit"
          anchor={anchor}
          startupName={data.startup.name}
          initialCondition={data.follow.condition}
          pending={editFollow.isPending}
          onOpenChange={(open) => !open && close()}
          onSubmit={(condition) =>
            editFollow.mutate(
              { path: { startupId }, body: { condition } },
              {
                onSuccess: () => {
                  trackEvent('follow_edited', {
                    startup_id: startupId,
                    with_condition: !!condition,
                  })
                  toast(copy('toast.edit.saved'))
                  close()
                },
                onError: () => toast(copy('toast.edit.error')),
              }
            )
          }
        />
      )}
      {overlay === 'follow' && (
        <FollowWindow
          open
          mode="follow"
          anchor={anchor}
          startupName={data.startup.name}
          suggested={suggested}
          allowanceLine={allowanceLine(data.allowance)}
          pending={followStartup.isPending}
          onOpenChange={(open) => !open && close()}
          onSubmit={(condition) =>
            followStartup.mutate(
              {
                body: {
                  ...(shareToken ? { shareToken } : { startupId }),
                  ...(condition && { condition }),
                },
              },
              {
                onSuccess: (response) => {
                  trackEvent('follow_started', {
                    startup_id: startupId,
                    with_condition: !!condition,
                    surface: 'panel',
                  })
                  toast(followToast(response.data, data.startup.name))
                  onFollowChange?.(startupId, true)
                  close()
                },
                onError: () => toast(copy('toast.follow.error', { startup: data.startup.name })),
              }
            )
          }
        />
      )}
      {overlay === 'stop' && (
        <StopDialog
          startupId={startupId}
          startupName={data.startup.name}
          waiting={data.state === 'WAITING_CHECK' || data.state === 'WAITING_PLACE'}
          onClose={close}
          onStopped={() => onFollowChange?.(startupId, false)}
        />
      )}
      <AssessmentWindow
        data={data}
        open={overlay === 'assessment'}
        onOpenChange={(open) => setOverlay(open ? 'assessment' : null)}
        onFollow={actions.onFollow}
      />
    </>
  )

  return { actions, dialogs, note: { editing: noteEditing, setEditing: setNoteEditing } }
}

/** The cards of a startup in the order the design gives them. */
export function RelationshipCards({
  data,
  variant,
  actions,
  note,
}: {
  data: Relationship
  variant: CardVariant
  actions: CardActions
  note: { editing: boolean; setEditing: (editing: boolean) => void }
}) {
  return (
    <>
      {data.state === 'NONE' && (
        <NoFollowCards data={data} variant={variant} actions={actions} part="follow" />
      )}
      <FollowingForCard data={data} variant={variant} actions={actions} />
      <SinceCard data={data} actions={actions} />
      {data.state === 'NONE' && <LatestUpdatesCard data={data} />}
      <PassedCard data={data} variant={variant} actions={actions} />
      {/* The AI's card always sits above the note; without an assessment it invites the investor to connect their AI. */}
      <AssessmentCard data={data} variant={variant} actions={actions} />
      <NoFollowCards data={data} variant={variant} actions={actions} part="ai" />
      <NoteCard data={data} editing={note.editing} onEditingChange={note.setEditing} />
    </>
  )
}
